/**
 * NovedadesTab — derived activity feed scoped to an investor (PR #7 read-only).
 *
 * Uses GET /activity with entity_type filters. We fetch a few entity scopes
 * and merge them client-side. Persistent read/unread tracking is intentionally
 * deferred to PR #8.
 */
import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";

const ACTION_LABEL = {
  "user.role_changed":          "Cambio de rol",
  "user.created":               "Usuario creado",
  "invitation.ok":              "Invitación enviada",
  "invitation.failed":          "Invitación con email fallido",
  "invitation.accepted":        "Cuenta activada",
  "milestone.updated":          "Hito actualizado",
  "construction_report.created":"Reporte de obra creado",
  "construction_report.updated":"Reporte de obra actualizado",
  "construction_report.deleted":"Reporte de obra eliminado",
  "payment.created":            "Pago creado",
  "payment.updated":            "Pago actualizado",
  "payment.archived":           "Pago archivado",
  "document.created":           "Documento creado",
  "document.updated":           "Documento actualizado",
  "document.deleted":           "Documento eliminado",
};

function labelFor(action) {
  return ACTION_LABEL[action] || action;
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function NovedadesTab({ investor, villas = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!investor) return;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const calls = [
          api.get("/activity", { params: { entity_type: "user", entity_id: investor.user_id, limit: 50 } }),
          ...villas.map((v) =>
            api.get("/activity", { params: { entity_type: "villa", entity_id: v.villa_id, limit: 50 } }),
          ),
        ];
        const results = await Promise.all(calls.map((p) => p.catch(() => ({ data: [] }))));
        const merged = [];
        const seen = new Set();
        results.forEach((r) => {
          (r.data || []).forEach((row) => {
            const id = row.activity_id || `${row.timestamp}:${row.action}:${row.entity_id}`;
            if (seen.has(id)) return;
            seen.add(id);
            merged.push(row);
          });
        });
        merged.sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
        setItems(merged.slice(0, 100));
      } catch (e) {
        setError(formatError(e));
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [investor, villas]);

  if (!investor) return null;

  return (
    <div data-testid="operaciones-tab-novedades">
      <div className="text-sm text-stone-600 mb-3">
        Actividad reciente relacionada con <span className="font-medium">{investor.name || investor.email}</span>
        {villas.length > 0 && ` y sus ${villas.length === 1 ? "villa" : "villas"}`}.
      </div>
      {error && (
        <div className="mb-3 text-sm px-3 py-2 rounded-md border bg-red-50 border-red-200 text-red-800">
          {error}
        </div>
      )}
      {loading ? (
        <div className="dv-card text-center py-6 text-stone-500 text-sm">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="dv-card text-center py-8 text-stone-500 text-sm">
          Aún no hay actividad registrada para este inversor.
        </div>
      ) : (
        <ol className="dv-card p-0 overflow-hidden divide-y divide-stone-100" data-testid="operaciones-novedades-list">
          {items.map((row, i) => (
            <li
              key={row.activity_id || i}
              className="px-4 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium">{labelFor(row.action)}</div>
                <div className="text-xs text-stone-500">
                  {row.entity_type} · {row.actor_name || row.actor_role || "Sistema"}
                </div>
              </div>
              <div className="text-xs text-stone-500 shrink-0">{fmt(row.timestamp)}</div>
            </li>
          ))}
        </ol>
      )}
      <div className="text-xs text-stone-400 mt-3">
        Notificaciones persistentes (leído/no leído) llegarán en una próxima entrega.
      </div>
    </div>
  );
}
